import React, { useEffect, useState } from 'react';
import { Link, navigate, useLocation } from '../router';
import { useAuth } from '../auth';
import { login, register } from '../hooks/useApi';
import { Brand } from '../components/Site';
import { GoogleIcon } from '../components/Icon';
import { plural } from '../utils/format';

const OAUTH_ERRORS: Record<string, string> = {
  password_account: 'That email already has a password account. Sign in with your email and password.',
  oauth_failed: 'Google sign-in failed. Please try again.',
  oauth_expired: 'Your sign-in took too long. Please try again.',
  google_disabled: 'Google sign-in is not available right now.',
};

function safeNext(next: string | null) {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/app';
}

export default function Login({ mode }: { mode: 'login' | 'signup' }) {
  const { search } = useLocation();
  const { me, config, refresh } = useAuth();
  const next = safeNext(search.get('next'));
  const isSignup = mode === 'signup';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(OAUTH_ERRORS[search.get('error') || ''] || '');

  useEffect(() => {
    if (me?.authenticated) navigate(next, { replace: true });
  }, [me, next]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (isSignup) await register(name, email, password);
      else await login(email, password);
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setBusy(false);
    }
  }

  const switchTo = `${isSignup ? '/login' : '/signup'}${search.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`;

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <Brand />
        <h1 className="auth-title">{isSignup ? 'Create your account' : 'Welcome back'}</h1>
        <p className="auth-sub">
          {isSignup ? `Free forever. ${plural(config!.freeMonthlyGrades, 'AI grade')} every month.` : 'Sign in to continue your revisions.'}
        </p>

        {config!.googleEnabled && (
          <>
            <a className="btn btn-google btn-lg" href={`/api/auth/google?next=${encodeURIComponent(next)}`}>
              <GoogleIcon /> Continue with Google
            </a>
            <div className="divider">or</div>
          </>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {isSignup && (
            <div className="field">
              <label className="field-label" htmlFor="name">Name</label>
              <input id="name" className="input" autoComplete="name" value={name} onChange={e => setName(e.target.value)} required maxLength={80} />
            </div>
          )}
          <div className="field">
            <label className="field-label" htmlFor="email">Email</label>
            <input id="email" className="input" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              minLength={isSignup ? 8 : undefined}
              required
            />
            {isSignup && <p className="field-help" style={{ margin: '6px 0 0' }}>At least 8 characters.</p>}
          </div>

          {error && <div className="alert alert-danger" style={{ marginTop: 16 }} role="alert">{error}</div>}

          <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
            {busy && <span className="spinner" />}
            {isSignup ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="auth-switch">
          {isSignup ? 'Already have an account? ' : "Don't have an account? "}
          <Link to={switchTo}>{isSignup ? 'Sign in' : 'Sign up free'}</Link>
        </p>
        {isSignup && (
          <p className="auth-legal">
            By creating an account you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
          </p>
        )}
      </div>
    </div>
  );
}
